/**
 * Searching for a RECORD, not a page (audit H11).
 *
 * Global search filtered a static list of destinations. Its placeholder said
 * "Search for pages, actions...", so typing a student's name and getting
 * nothing was consistent with what it advertised — and useless at a front
 * desk, where finding one student, family or invoice is the most frequent
 * thing anyone does.
 *
 * The servers already do the matching: students on name OR student number,
 * families on name, invoices on family name OR invoice number, each behind
 * its own permission and scoped to the tenant. Nothing here is a new search
 * capability; it is the search box finally asking.
 *
 * What a result SHOWS is an allow-list: a name, an identifying number, and
 * just enough to tell two similar records apart. Nothing sensitive is
 * rendered, and because each queryFn maps before it returns, nothing
 * sensitive is held in the query cache either.
 *
 * What it does not change: the list endpoints themselves still send whatever
 * they always sent to these roles — the students list includes allergies and
 * medical notes in its payload. This feature asks those endpoints from more
 * places than the Students page did, so a lean projection for search is worth
 * doing; it is a backend change and deliberately not smuggled in here.
 */

import { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { studentsApi } from "@/api/students";
import { familiesApi } from "@/api/families";
import { invoicesApi } from "@/api/invoices";
import { useAuthStore } from "@/store/authStore";
import { capitalize, formatCurrency } from "@/utils/formatters";

/** Below this, a query matches most of the school and helps nobody. */
export const MIN_QUERY_LENGTH = 2;

/** How many of each kind to show before the list stops being scannable. */
export const PER_KIND_LIMIT = 5;

/**
 * What one kind's search came back with.
 *
 * `total` matters as much as the rows: the server orders students by surname,
 * not by relevance, so the sixth Ahmad is simply absent. Dropping him without
 * a word recreates the audit's original symptom — a search that says nothing
 * matched when something did.
 */
export interface KindResult {
  hits: RecordHit[];
  total: number;
}

export type RecordKind = "student" | "family" | "invoice";

export interface RecordHit {
  kind: RecordKind;
  id: string;
  /** What the person typed at, e.g. the student's name. */
  label: string;
  /**
   * How to tell this one from the next one with the same name. Siblings share
   * a family; two families share a surname; two invoices share a month.
   */
  context?: string;
  path: string;
}

/**
 * Which record kinds this user may search.
 *
 * Each kind is gated on exactly the permission that already guards its page
 * and its sidebar entry, so search reaches what the person can already open
 * and nothing more. A teacher holds manage:students, so a teacher can find a
 * student by name — the same student whose page is one click away in the nav
 * today — and the students endpoint scopes a teacher to the students in the
 * classes they are assigned, so "find a student" means their own. A parent
 * holds none of the three and keeps the destination list.
 *
 * This is not the security boundary: every endpoint checks the same permission
 * and scopes to the tenant, so a mistake here costs a wasted request, not a
 * leak. See useRecordSearch.test.ts, which asserts this against the real
 * ROLE_PERMISSIONS map rather than a hand-made one.
 */
export const searchableKinds = (
  hasPermission: (permission: string) => boolean,
): Record<RecordKind, boolean> => ({
  student: hasPermission("manage:students"),
  family: hasPermission("manage:families"),
  invoice: hasPermission("manage:fees"),
});

const SCOPE_NOUN: Record<RecordKind, string> = {
  student: "students",
  family: "families",
  invoice: "invoices",
};

/** "a", "a and b", "a, b and c" */
const joinAnd = (words: string[]): string =>
  words.length <= 1
    ? (words[0] ?? "")
    : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;

export interface SearchScopeCopy {
  /** The box's placeholder, e.g. "Search students, families, invoices and pages…" */
  placeholder: string;
  /** The input's accessible name. */
  ariaLabel: string;
  /** The dialog's screen-reader description (first sentence). */
  description: string;
  /**
   * Shown while the query is still too short for a record search; null when
   * this person can't search any records (a parent), so there is nothing to
   * keep typing for.
   */
  keepTyping: string | null;
}

/**
 * What the search box says it searches — only what this person can find.
 *
 * A parent or teacher reading "Search students, families, invoices" and then
 * never seeing a family or an invoice is being told something untrue.
 */
export const describeSearchScope = (
  kinds: Record<RecordKind, boolean>,
): SearchScopeCopy => {
  const records = (Object.keys(SCOPE_NOUN) as RecordKind[])
    .filter((k) => kinds[k])
    .map((k) => SCOPE_NOUN[k]);
  return {
    placeholder: `Search ${joinAnd([...records, "pages"])}…`,
    ariaLabel: `Search ${joinAnd([...records, "pages"])}`,
    description: `Search ${joinAnd([...records, "pages", "quick actions"])}.`,
    keepTyping: records.length > 0 ? `Keep typing to search ${joinAnd(records)}` : null,
  };
};

/**
 * @param query what the person typed (already debounced by the caller)
 * @param active false when the dialog is closed. The dialog is mounted for
 *   the life of the page, so without this the last term stays "enabled" and
 *   every window focus refetches three searches nobody is looking at.
 */
export const useRecordSearch = (query: string, active = true) => {
  const { hasPermission } = useAuthStore();
  const term = active ? query.trim() : "";
  const longEnough = term.length >= MIN_QUERY_LENGTH;
  const allowed = searchableKinds(hasPermission);

  // Each queryFn maps before it caches, so the React Query cache holds the
  // five display fields and not a student's allergies.
  //
  // campus_id: null on purpose. The banner's campus filter scopes the LISTS a
  // person is browsing; someone searching a name at the front desk wants the
  // student found, not a lesson about which tab they were on. The record's own
  // page still shows where they belong.
  const students = useQuery({
    queryKey: ["record-search", "students", term],
    queryFn: async () => {
      const res = await studentsApi.list({
        search: term,
        campus_id: null,
        limit: PER_KIND_LIMIT,
        // A search finds anyone the person may open: a teacher covering
        // another teacher's group must find those students by name too.
        scope: "class",
      });
      return { hits: toStudentHits(res.data), total: readTotal(res.data) };
    },
    enabled: longEnough && allowed.student,
    staleTime: 30_000,
    // Hold the previous term's rows while the next one is in flight, so the
    // list does not collapse to empty between keystrokes.
    placeholderData: keepPreviousData,
  });

  const families = useQuery({
    queryKey: ["record-search", "families", term],
    queryFn: async () => {
      const res = await familiesApi.list({
        search: term,
        campus_id: null,
        limit: PER_KIND_LIMIT,
      });
      return { hits: toFamilyHits(res.data), total: readTotal(res.data) };
    },
    enabled: longEnough && allowed.family,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const invoices = useQuery({
    queryKey: ["record-search", "invoices", term],
    queryFn: async () => {
      const res = await invoicesApi.list({ search: term, limit: PER_KIND_LIMIT });
      return { hits: toInvoiceHits(res.data), total: readTotal(res.data) };
    },
    enabled: longEnough && allowed.invoice,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  // Stable identity while nothing has resolved, so the dialog's own memos on
  // `hits` are worth their keep.
  const hits = useMemo<RecordHit[]>(
    () =>
      // Backspacing below the minimum stops the search; keepPreviousData would
      // otherwise leave the last term's records on screen under a query that
      // is no longer being asked.
      longEnough
        ? [
            ...(allowed.student ? (students.data?.hits ?? []) : []),
            ...(allowed.family ? (families.data?.hits ?? []) : []),
            ...(allowed.invoice ? (invoices.data?.hits ?? []) : []),
          ]
        : [],
    [
      longEnough,
      students.data,
      families.data,
      invoices.data,
      allowed.student,
      allowed.family,
      allowed.invoice,
    ],
  );

  /**
   * How many matches of each kind did not fit. Zero when the search is not
   * truncated, which is the ordinary case.
   */
  const notShown = useMemo<Record<RecordKind, number>>(
    () => ({
      student: unshown(longEnough && allowed.student ? students.data : undefined),
      family: unshown(longEnough && allowed.family ? families.data : undefined),
      invoice: unshown(longEnough && allowed.invoice ? invoices.data : undefined),
    }),
    [
      longEnough,
      students.data,
      families.data,
      invoices.data,
      allowed.student,
      allowed.family,
      allowed.invoice,
    ],
  );

  const asked = longEnough && (allowed.student || allowed.family || allowed.invoice);

  /**
   * Still working. `isPending` covers the render between "enabled" and the
   * effect that starts the request — without it the dialog flashes "nothing
   * matched" before it has asked anything.
   */
  const isSearching =
    (allowed.student && (students.isFetching || students.isPending)) ||
    (allowed.family && (families.isFetching || families.isPending)) ||
    (allowed.invoice && (invoices.isFetching || invoices.isPending));

  /**
   * True when at least one search this user may run failed. Gated on `asked`
   * so a stale failure cannot keep warning after the box is emptied.
   */
  const isError =
    (allowed.student && students.isError) ||
    (allowed.family && families.isError) ||
    (allowed.invoice && invoices.isError);

  /** Which kinds failed, so the message can name them instead of guessing. */
  const failedKinds: RecordKind[] = [
    allowed.student && students.isError ? ("student" as const) : null,
    allowed.family && families.isError ? ("family" as const) : null,
    allowed.invoice && invoices.isError ? ("invoice" as const) : null,
  ].filter((k): k is RecordKind => k !== null);

  return {
    hits,
    notShown,
    failedKinds: asked ? failedKinds : [],
    isSearching: asked && isSearching,
    isError: asked && isError,
    /**
     * True when every search this user is allowed to run has come back empty.
     * Distinguished from "not asked yet" and from "asked and failed", so the
     * dialog never tells someone a student does not exist when the truth is
     * that the request errored.
     */
    searchedAndFoundNothing:
      asked && !isSearching && !isError && hits.length === 0,
  };
};

interface StudentLike {
  id: string;
  first_name: string;
  last_name: string;
  student_number?: string;
  family_name?: string;
  status?: string;
}
interface FamilyLike {
  id: string;
  name: string;
  student_count?: number;
  status?: string;
}
/** Shaped from the API, not from hope: the list returns `total` and a nested `family`. */
interface InvoiceLike {
  id: string;
  invoice_number: string;
  total?: number | string;
  family?: { name?: string } | null;
  status?: string;
}

/** The paginated envelope's count, absent or malformed responses included. */
function readTotal(data: unknown): number {
  const total = (data as { meta?: { total?: unknown } } | undefined)?.meta?.total;
  return typeof total === "number" && total >= 0 ? total : 0;
}

/** How many matches the server has beyond the ones we asked it for. */
function unshown(result: KindResult | undefined): number {
  if (!result) return 0;
  return Math.max(0, result.total - result.hits.length);
}

/**
 * A record that is no longer current must say so.
 *
 * A merge leaves an archived family behind, and a withdrawn student looks
 * exactly like an active sibling. Every list in this app labels those; a
 * finder that hands them back unlabelled is how someone bills the wrong
 * family. Active is the norm and stays unsaid.
 */
const notCurrent = (status?: string): string | undefined =>
  !status || status === "active" ? undefined : capitalize(status);

/**
 * One context line, or nothing at all. An empty string would make MUI render
 * an empty secondary paragraph under the name.
 */
const joinContext = (parts: (string | false | undefined)[]): string | undefined =>
  parts.filter(Boolean).join(" · ") || undefined;

/** Siblings share a family name, so lead with the number that is unique. */
export function toStudentHits(data: unknown): RecordHit[] {
  const students = (data as { students?: StudentLike[] } | undefined)?.students ?? [];
  return students.map((s) => ({
    kind: "student" as const,
    id: s.id,
    label: `${s.first_name} ${s.last_name}`.trim(),
    context: joinContext([
      notCurrent(s.status),
      s.student_number,
      s.family_name && `${s.family_name} family`,
    ]),
    path: `/students/${s.id}`,
  }));
}

export function toFamilyHits(data: unknown): RecordHit[] {
  const families = (data as { families?: FamilyLike[] } | undefined)?.families ?? [];
  return families.map((f) => ({
    kind: "family" as const,
    id: f.id,
    label: f.name,
    context: joinContext([
      notCurrent(f.status),
      f.student_count === undefined
        ? undefined
        : f.student_count === 1
          ? "1 student"
          : `${f.student_count} students`,
    ]),
    path: `/families/${f.id}`,
  }));
}

export function toInvoiceHits(data: unknown): RecordHit[] {
  const invoices = (data as { invoices?: InvoiceLike[] } | undefined)?.invoices ?? [];
  return invoices.map((i) => ({
    kind: "invoice" as const,
    id: i.id,
    label: i.invoice_number,
    // Unlike a student, an invoice has no "ordinary" state to leave unsaid:
    // whether it is paid, overdue or void is the first thing anyone needs,
    // and showing a voided invoice as just "Smith · $600.00" invites someone
    // to chase money that was written off last week.
    context: joinContext([
      i.family?.name,
      i.total != null ? formatCurrency(i.total) : undefined,
      capitalize((i.status ?? "").replace(/_/g, " ")) || undefined,
    ]),
    path: `/invoices/${i.id}`,
  }));
}
